<script setup>
import { computed } from 'vue';

const props = defineProps({
  label: {
    type: String,
    required: true,
  },
  value: {
    type: String,
    required: true,
  },
  infoText: {
    type: String,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  trend: {
    type: Number,
    default: null,
  },
  invertTrend: {
    type: Boolean,
    default: false,
  },
});

const hasTrend = computed(() => props.trend !== null && props.trend !== 0);

const trendColor = computed(() => {
  if (!hasTrend.value) return '';
  const isNegative = props.trend < 0;
  // For inverted metrics (like handoff rate), a decrease is good
  const isGood = props.invertTrend ? isNegative : !isNegative;
  return isGood
    ? 'border-n-teal-10 text-n-teal-10'
    : 'border-n-ruby-9 text-n-ruby-9';
});

const trendValue = computed(() => Math.abs(Math.round(props.trend)));
</script>

<template>
  <div
    data-test-id="reportMetricContainer"
    :class="{
      'grayscale pointer-events-none opacity-30': disabled,
    }"
  >
    <h3 class="flex items-center m-0 text-sm font-medium text-n-slate-11">
      <span data-test-id="reportMetricLabel">{{ label }}</span>
      <fluent-icon
        v-tooltip="infoText"
        data-test-id="reportMetricInfo"
        size="14"
        icon="info"
        class="text-n-slate-11 my-0 mx-1 mt-0.5"
      />
    </h3>
    <div class="flex items-end mt-1">
      <h4
        data-test-id="reportMetricValue"
        class="mb-0 text-2xl text-n-slate-12"
      >
        {{ value }}
      </h4>
      <div
        v-if="hasTrend"
        data-test-id="reportMetricTrend"
        class="text-xs ml-2 flex items-center mb-1"
      >
        <div
          v-if="trend < 0"
          class="h-0 w-0 border-x-4 border-x-transparent border-t-[8px] mr-1"
          :class="trendColor"
        />
        <div
          v-else
          class="h-0 w-0 border-x-4 border-x-transparent border-b-[8px] mr-1"
          :class="trendColor"
        />
        <span class="font-medium" :class="trendColor"> {{ trendValue }}% </span>
      </div>
    </div>
  </div>
</template>
